// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import type { ClimateData, Station, StationDetail } from './types';

let cache: ClimateData | null = null;
const detailCache = new Map<string, StationDetail>();

export async function loadData(): Promise<ClimateData> {
  if (cache) return cache;
  const r = await fetch('data/climate.json');
  if (!r.ok) throw new Error(`Could not load data (HTTP ${r.status})`);
  cache = (await r.json()) as ClimateData;
  return cache;
}

export async function loadStationDetail(id: string): Promise<StationDetail> {
  const hit = detailCache.get(id);
  if (hit) return hit;
  const r = await fetch(`data/stations/${id}.json`);
  if (!r.ok) throw new Error(`Could not load station ${id}`);
  const d = (await r.json()) as StationDetail;
  detailCache.set(id, d);
  return d;
}

export function stationById(data: ClimateData, id: string): Station | undefined {
  return data.stations.find((s) => s.id === id);
}

/** Ordinary-least-squares slope + intercept over [x,y] pairs (client-side, for fit lines). */
export function ols(points: Array<[number, number]>): { slope: number; intercept: number } | null {
  const pts = points.filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]));
  const n = pts.length;
  if (n < 2) return null;
  let sx = 0, sy = 0, sxx = 0, sxy = 0;
  for (const [x, y] of pts) { sx += x; sy += y; sxx += x * x; sxy += x * y; }
  const denom = n * sxx - sx * sx;
  if (denom === 0) return null;
  const slope = (n * sxy - sx * sy) / denom;
  return { slope, intercept: (sy - slope * sx) / n };
}

export function median(values: number[]): number {
  const a = values.filter((v) => Number.isFinite(v)).sort((x, y) => x - y);
  if (!a.length) return NaN;
  const m = Math.floor(a.length / 2);
  return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
}
